import React, { useState, useEffect } from 'react';
import { ArrowLeft, RotateCcw, CheckCircle, XCircle, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { vocabularyAPI, progressAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const MultipleChoice = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [timer, setTimer] = useState(30);
  const [gameComplete, setGameComplete] = useState(false);
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadVocabulary();
  }, []);

  const loadVocabulary = async () => {
    try {
      setLoading(true);
      const response = await vocabularyAPI.getAll();
      const vocabularyWords = response.words || [];
      setWords(vocabularyWords);
      generateQuestions(vocabularyWords);
    } catch (error) {
      toast({
        title: "Error Loading Vocabulary",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Generate multiple choice questions from vocabulary
  const generateQuestions = (vocabularyWords = words) => {
    if (vocabularyWords.length === 0) return;
    
    // Randomly shuffle and select 10 words from the vocabulary
    const shuffledWords = [...vocabularyWords].sort(() => 0.5 - Math.random());
    const selectedWords = shuffledWords.slice(0, Math.min(10, shuffledWords.length));
    
    const questionSet = selectedWords.map((word, index) => {
      const correctAnswer = word.englishTranslation;
      const wrongAnswers = vocabularyWords
        .filter(w => w.id !== word.id)
        .map(w => w.englishTranslation)
        .sort(() => 0.5 - Math.random())
        .slice(0, 3);
      
      const allOptions = [correctAnswer, ...wrongAnswers].sort(() => 0.5 - Math.random());
      const correctIndex = allOptions.indexOf(correctAnswer);

      return {
        id: index + 1,
        question: `What does "${word.algerianWord}" mean in English?`,
        options: allOptions,
        correctAnswer: correctIndex,
        word: word
      };
    });
    
    // Shuffle the questions themselves for additional randomness
    const shuffledQuestions = questionSet.sort(() => 0.5 - Math.random());
    setQuestions(shuffledQuestions);
  };

  useEffect(() => {
    if (timer > 0 && !showResult && !gameComplete) {
      const interval = setInterval(() => {
        setTimer(timer - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else if (timer === 0 && !showResult) {
      handleTimeUp();
    }
  }, [timer, showResult, gameComplete]);

  const handleTimeUp = () => {
    setSelectedAnswer(null);
    setShowResult(true);
    toast({
      title: "Time's up!",
      description: "Moving to next question",
      variant: "destructive"
    });
  };

  const handleAnswerSelect = (answerIndex) => {
    if (showResult) return;
    
    setSelectedAnswer(answerIndex);
    setShowResult(true);
    
    const isCorrect = answerIndex === questions[currentQuestion].correctAnswer;
    if (isCorrect) {
      setScore(score + 1);
      toast({
        title: "Correct!",
        description: "Well done! 🎉",
      });
    } else {
      toast({
        title: "Not quite right",
        description: `The correct answer is "${questions[currentQuestion].options[questions[currentQuestion].correctAnswer]}"`,
        variant: "destructive"
      });
    }
  };

  const nextQuestion = async () => {
    // Update progress for current question before moving to next
    const currentQ = questions[currentQuestion];
    const isCorrect = selectedAnswer === currentQ.correctAnswer;
    
    if (currentQ && currentQ.word) {
      try {
        await progressAPI.update({
          category: currentQ.word.category,
          wordId: currentQ.word.id,
          gameType: "quiz",
          score: isCorrect ? 1 : 0,
          completed: true
        });
      } catch (error) {
        console.error('Failed to update progress:', error);
      }
    }
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowResult(false);
      setTimer(30);
    } else {
      setGameComplete(true);
      toast({
        title: "Quiz Complete!",
        description: `Final Score: ${score + (selectedAnswer === questions[currentQuestion].correctAnswer ? 1 : 0)}/${questions.length}`,
      });
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setScore(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setTimer(30);
    setGameComplete(false);
    generateQuestions(words);
  };

  const getScoreColor = () => {
    const percentage = (score / questions.length) * 100;
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (questions.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Loading Quiz...</h1>
      </div>
    );
  }

  if (gameComplete) {
    const finalScore = score + (selectedAnswer === questions[currentQuestion]?.correctAnswer ? 1 : 0);
    const percentage = Math.round((finalScore / questions.length) * 100);
    
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <Card className="max-w-md mx-auto shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl">Quiz Complete!</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-4xl font-bold mb-4 ${getScoreColor()}`}>
                {finalScore}/{questions.length}
              </div>
              <div className={`text-2xl font-semibold mb-6 ${getScoreColor()}`}>
                {percentage}%
              </div>
              
              <div className="space-y-4">
                <div className="text-lg">
                  {percentage >= 80 ? "Excellent work! 🎉" : 
                   percentage >= 60 ? "Good job! Keep practicing 👍" : 
                   "Keep studying and try again! 📚"}
                </div>
                
                <div className="flex space-x-4">
                  <Button onClick={resetQuiz} className="flex-1">
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Link to="/games" className="flex-1">
                    <Button variant="outline" className="w-full">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Games
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Link to="/games">
          <Button variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Games
          </Button>
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Multiple Choice Quiz</h1>
        <Button variant="outline" size="sm" onClick={resetQuiz}>
          <RotateCcw className="w-4 h-4 mr-2" />
          Reset
        </Button>
      </div>

      {/* Progress and Stats */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600">
            Question {currentQuestion + 1} of {questions.length}
          </span>
          <div className="flex items-center space-x-4">
            <div className={`flex items-center space-x-1 ${timer <= 10 ? 'text-red-600' : 'text-gray-600'}`}>
              <Clock className="w-4 h-4" />
              <span className="font-mono text-sm">{timer}s</span>
            </div>
            <span className="text-sm text-gray-600">
              Score: {score}/{questions.length}
            </span>
          </div>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Question */}
      <Card className="mb-8 shadow-lg">
        <CardHeader>
          <CardTitle className="text-xl text-center">
            {currentQ.question}
          </CardTitle>
          {currentQ.word.pronunciation && (
            <p className="text-center text-gray-500 italic">
              Pronunciation: {currentQ.word.pronunciation}
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {currentQ.options.map((option, index) => {
            let buttonClass = "w-full text-left justify-start h-auto py-4 px-4 text-wrap";
            
            if (showResult) {
              if (index === currentQ.correctAnswer) {
                buttonClass += " bg-green-100 border-green-500 text-green-800 hover:bg-green-100";
              } else if (index === selectedAnswer && selectedAnswer !== currentQ.correctAnswer) {
                buttonClass += " bg-red-100 border-red-500 text-red-800 hover:bg-red-100";
              } else {
                buttonClass += " opacity-50";
              }
            } else {
              buttonClass += " hover:bg-gray-50 border-gray-200";
            }
            
            return (
              <Button
                key={index}
                variant="outline"
                className={buttonClass}
                onClick={() => handleAnswerSelect(index)}
                disabled={showResult}
              >
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full text-sm font-semibold">
                    {String.fromCharCode(65 + index)}
                  </div>
                  <span>{option}</span>
                  {showResult && index === currentQ.correctAnswer && (
                    <CheckCircle className="w-5 h-5 text-green-600 ml-auto" />
                  )}
                  {showResult && index === selectedAnswer && selectedAnswer !== currentQ.correctAnswer && (
                    <XCircle className="w-5 h-5 text-red-600 ml-auto" />
                  )}
                </div>
              </Button>
            );
          })}
        </CardContent>
      </Card>

      {/* Example Sentence */}
      {showResult && currentQ.word.exampleSentence && (
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-blue-800 font-medium mb-1">Example:</p>
            <p className="text-blue-700 italic">"{currentQ.word.exampleSentence}"</p>
          </CardContent>
        </Card>
      )}

      {/* Next Button */}
      {showResult && (
        <div className="text-center">
          <Button onClick={nextQuestion} size="lg">
            {currentQuestion < questions.length - 1 ? 'Next Question' : 'View Results'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default MultipleChoice;